import React, { useState } from 'react';
import PageHeader from 'Components/PageHeader';
import { useTranslation } from 'react-i18next';
import useGetSimulationById from 'Hooks/query-hooks/simulations/useGetSimulationById';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import { useParams } from 'react-router-dom';
import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import _ from 'lodash';
import clsx from 'clsx';
import { shouldShowMerchants, shouldShowCompareSimulations } from 'Selectors';
import { RoleService, NumberService } from 'Services';
import { SORT_VIEW } from 'Common/enums';
import appStyles from 'Routes/Routes.css';
import useCubeSimulationQuery from 'Hooks/query-hooks/simulations/useCubeSimulationQuery';
import DeleteSimulation from './components/DeleteSimulation';
import RuleSimulationDashlet from './components/RuleSimulationDashlet';
import HeaderRows from './components/HeaderRows';
import { rulePerformanceRejectColumns, rulePerformanceAllColumns } from './utils';
import ExportCSV from './components/ExportCSV';

import styles from './ViewSimulation.css';

const ViewSimulation = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const simulationAPI = useGetSimulationById(parseInt(id, 10));
    const deployedVersion = simulationAPI.data?.deployedVersion;
    const compare = simulationAPI.data?.compare;
    const simulationReportProperties: ProcessedSimulationReportFormValues = simulationAPI.data;
    const simulationId = simulationReportProperties?.simulationId;
    const { hasError, errorCmp } = errorController([simulationAPI.error]);
    const { users } = useGetReduxUsers();
    const [sortView, setSortView] = useState<string>(SORT_VIEW.ALPHANUMERICAL);
    const [filterView, setFilterView] = useState('All');
    // FIXME: state rejectedTransactionCount not used.
    const [, setRejectedTransactionCount] = useState(0);
    const [totalTransactionCount, setTotalTransactionCount] = useState(0);
    const dashletsRowCN = clsx(styles.flexRow, styles.chartsRow);
    const compareSimulationId = compare?.length ? compare?.[0] : '';
    const sandboxData = useCubeSimulationQuery(simulationId, simulationReportProperties);
    const deployedData = useCubeSimulationQuery(compareSimulationId, simulationReportProperties);
    const subjectString = shouldShowMerchants() ? `, ${t('All Merchants')}` : '';

    const renderDashlets = () => {
        return (
            <div className={dashletsRowCN}>
                <RuleSimulationDashlet
                    formValues={simulationReportProperties}
                    className={styles.top5VolumeDashletContainer}
                    minColumns={rulePerformanceRejectColumns}
                    chartType='reject'
                    filterView={filterView as FilterViewOptions}
                    sortView={sortView as SortType}
                    setRejectedTransactionCount={setRejectedTransactionCount}
                    totalTransactionCount={totalTransactionCount}
                    sandboxData={sandboxData}
                    compareData={deployedData}
                />
                <RuleSimulationDashlet
                    formValues={simulationReportProperties}
                    className={styles.top5ValueDashletContainer}
                    minColumns={rulePerformanceAllColumns}
                    chartType='all'
                    filterView={filterView as FilterViewOptions}
                    sortView={sortView as SortType}
                    setTotalTransactionCount={setTotalTransactionCount}
                    totalTransactionCount={totalTransactionCount}
                    sandboxData={sandboxData}
                    compareData={deployedData}
                />
            </div>
        );
    };

    if (hasError) return <>{errorCmp}</>;

    if (simulationAPI.isLoading || simulationAPI.isFetching || _.isUndefined(simulationAPI.data)) {
        return <RouteLoading />;
    }

    return (
        <div className={appStyles.landingPage}>
            <PageHeader className={styles.pageHeaderWrapper}>
                <PageHeader.Left className={styles.titleText}>
                    {`${t('viewSimulationHeader', {
                        simulationType: simulationAPI.data?.simulationType,
                        sandbox: simulationAPI.data.sandbox
                    })}${subjectString}`}
                </PageHeader.Left>
                <PageHeader.Right>
                    <ExportCSV recordId={id} reportProperties={simulationReportProperties} />
                    {RoleService.hasEditRuleOrBulkUploadAccess && (
                        <DeleteSimulation activeSimulationData={simulationAPI.data} />
                    )}
                </PageHeader.Right>
            </PageHeader>
            <div className={styles.reportViewContainer}>
                {simulationReportProperties ? (
                    <>
                        {simulationReportProperties && (
                            <HeaderRows
                                newlyGenerated={false}
                                formValues={simulationReportProperties}
                                filterView={filterView as FilterViewOptions}
                                setFilterView={setFilterView}
                                sortView={sortView as SortType}
                                setSortView={setSortView}
                                totalTransactions={NumberService.getLocaleNumber(totalTransactionCount)}
                                users={users}
                                deployedVersion={deployedVersion}
                                compare={compare}
                                hideMerchantDataCard={!shouldShowMerchants()}
                                hideCompareDataCard={!shouldShowCompareSimulations()}
                            />
                        )}
                        {renderDashlets()}
                    </>
                ) : (
                    <>
                        <div>{t('newMerchantReportInstructions')}</div>
                        <div className={styles.newReportInstructions}>{t('newMerchantReportInstructionsPt2')}</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ViewSimulation;
